import { useState, useEffect, useRef } from 'react';
import CloseIcon from '../../../assets/icons/close.svg?react';
import TimeInput from '../../../components/TimeInput';
import { floatInputFormatter } from '../../../utils/utils';
import dayjs from 'dayjs';
import { useSendSms } from '../../../hooks/queries/smsQueries';
const SmsSendModal = ({ onClose }) => {
  const { mutateAsync: sendSms, isPending } = useSendSms();
  const [step, setStep] = useState(1); // 1: 입력, 2: 확인
  const [formData, setFormData] = useState({
    startTime: '00:00',
    endTime: '00:00',
    capacity: '',
  });
  const timeoutRef = useRef(null);

  // 시간을 분으로 변환하는 헬퍼 함수
  const timeToMinutes = (time) => {
    if (!time || !time.includes(':')) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // 시간 검증 로직
  useEffect(() => {
    // 타이머 초기화
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 두 시간이 모두 입력된 경우에만 검증
    if (formData.startTime && formData.endTime) {
      timeoutRef.current = setTimeout(() => {
        const startMinutes = timeToMinutes(formData.startTime);
        const endMinutes = timeToMinutes(formData.endTime);

        // 시작시간이 종료시간보다 늦은 경우
        if (startMinutes > endMinutes) {
          setFormData(prev => ({
            ...prev,
            endTime: prev.startTime
          }));
        }
        // 종료시간이 시작시간보다 빠른 경우
        else if (endMinutes < startMinutes) {
          setFormData(prev => ({
            ...prev,
            startTime: prev.endTime
          }));
        }
      }, 800); // 0.8초 지연
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formData.startTime, formData.endTime]);

  const handleTimeChange = (field) => (value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNumberChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
  };

  const isFormValid = 
    formData.startTime && 
    formData.endTime && 
    formData.capacity;

  const handleNext = () => {
    if (!isFormValid) return;
    setStep(2);
  };

  const handlePrevious = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    try {
    const today = dayjs();
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    await sendSms({
      drType: '국민DR',
      target: '국민DR 자원 전체 (EWP-26IR-J01)',
      dateInfo: `${today.month() + 1}월 ${today.date()}일 (${weekdays[today.day()]})요일 ${formData.startTime} - ${formData.endTime}`,
      capacity: `${formData.capacity}MW`
    }, {
      onSuccess: () => {
        alert('SMS 발송이 완료되었습니다.');
        onClose();
      }
    });
    } catch (error) {
      console.error('SMS 발송 실패:', error);
      alert('SMS 발송에 실패했습니다.');
      onClose();
    }
  };

  // Payload 데이터 생성
  const getPayloadData = () => {
    const today = dayjs();
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    
    return {
      drType: '국민DR',
      target: '국민DR 자원 전체 (EWP-26IR-J01)',
      dateInfo: `${today.month() + 1}월 ${today.date()}일 (${weekdays[today.day()]})요일 ${formData.startTime} - ${formData.endTime}`,
      capacity: `${formData.capacity}MW`
    };
  };

  const payload = step === 2 ? getPayloadData() : null;

  return (
    <div className="modal-background">
      <div className="modal-container overflow-visible">
        <div className="modal-header">
          <h3>SMS 발송</h3>
          <button className="close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="modal-content">
          {step === 1 ? (
            // Step 1: 입력 폼
            <>
              <table className='transparent-table w-full'>
                <tbody>
                  <tr>
                    <td>
                      <label htmlFor="startTime" className='w-20 mr-10 text-b2-700 required'>
                        시작시간
                      </label>
                    </td>
                    <td>
                      <TimeInput
                        value={formData.startTime}
                        onChange={handleTimeChange('startTime')}
                        placeholder="HH:mm"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label htmlFor="endTime" className='w-20 mr-10 text-b2-700 required'>
                        종료시간
                      </label>
                    </td>
                    <td>
                      <TimeInput
                        value={formData.endTime}
                        onChange={handleTimeChange('endTime')}
                        placeholder="HH:mm"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label htmlFor="capacity" className='w-20 mr-10 text-b2-700 required'>
                        감축용량
                      </label>
                    </td>
                    <td>
                      <div className="flex align-center gap-5">
                        <input
                          type="text"
                          id="capacity"
                          value={formData.capacity}
                          onInput={(e) => floatInputFormatter(e)}
                          onChange={handleNumberChange}
                          className='flex-1 input-primary'
                          placeholder='감축용량을 입력해주세요.'
                          autoComplete='off'
                        />
                        <span className="text-b2-700 text-gray-700">MW</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className='flex justify-end w-full mt-20'>
                <button 
                  className='btn big-btn btn-primary flex-1' 
                  onClick={handleNext}
                  disabled={!isFormValid}
                >
                  다음
                </button>
              </div>
            </>
          ) : (
            // Step 2: 확인 화면
            <>
              <div className="my-10">
                <p className="text-b2-400 text-gray-600">아래 내용으로 <span className="text-b2-700 text-primary">등록된 모든 연락처</span>에게 SMS가 발송됩니다.</p>
              </div>
              <table className='transparent-table w-full'>
                <tbody>
                  <tr>
                    <td>
                      <label className='w-20 mr-10 text-b2-700'>제도</label>
                    </td>
                    <td>
                      <p className='text-b2-400 text-gray-800'>{payload.drType}</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label className='w-20 mr-10 text-b2-700'>대상</label>
                    </td>
                    <td>
                      <p className='text-b2-400 text-gray-800'>{payload.target}</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label className='w-20 mr-10 text-b2-700'>시간</label>
                    </td>
                    <td>
                      <p className='text-b2-400 text-gray-800'>{payload.dateInfo}</p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label className='w-20 mr-10 text-b2-700'>용량</label>
                    </td>
                    <td>
                      <p className='text-b2-400 text-gray-800'>{payload.capacity}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className='flex gap-10 w-full mt-20'>
                <button 
                  className='btn big-btn btn-style-5 flex-1' 
                  onClick={handlePrevious}
                >
                  이전
                </button>
                <button 
                  className='btn big-btn btn-primary flex-1' 
                  onClick={handleSubmit}
                  disabled={isPending}
                >
                  {isPending ? '발송 중...' : 'SMS 발송'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmsSendModal;
